import { useEffect, useState, useCallback } from "react";
import { Params, useParams, useNavigate } from "react-router-dom";

import "./style.scss";
import { ReactComponent as TrashIcon } from "../../assets/trash.icon.svg";
import { ReactComponent as MoonIcon } from "../../assets/moon.icon.svg";
import { ReactComponent as TivaPlaceholder } from "../../assets/placeholder-tiva.svg";
import LeftMenuLayout from "../../components/layout/left-menu-layout.comp";
import ChatList from "../../components/chat-list/chat-list.comp";
import CreateChatMessages from "../create-chat-messages/create-chat-messages";
import { ChatMessages } from "../chat-messages/chat-messages";
import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";
import { ThemeContainer } from "../../common/containers/theme/theme.container";
import { User } from "../../common/interfaces/comps/user.interface";
import { Chat } from "../../common/interfaces/chats/chat.interface";
import { fetchRooms, archiveRoom, archiveAllRooms } from "../../api/chats/endpoints-referrers";
import { DropdownMenuItem } from "../../components/dropdown-menu/dropdown-menu.comp";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../components/confirmation-popup/confirmation-popup.comp";
import HeaderLayout from "../../components/layout/header-layout.comp";
import Footer from "../../components/footer/footer.comp";

const ChatBot = ({ isNewChat = false }: { isNewChat?: boolean }) => {
    const navigate = useNavigate();
    const { chatId } = useParams<Params<string>>();
    const { user, isAdmin }: { user: User | null; isAdmin?: boolean } =
        FirebaseAuthContainer.useContainer();
    const { leftMenuCollapsed, isLightTheme, changeTheme } = ThemeContainer.useContainer();
    const [settings, setSettings] = useState<DropdownMenuItem[]>([]);
    const [chats, setChats] = useState<Chat[]>([]);
    const [settingChat, setSettingChat] = useState<Chat>();
    const [newChat, setNewChat] = useState<Chat | null>(null);
    const [newMessage, setNewMessage] = useState<string | null>(null);
    const [removeChatConfirmationOpen, setRemoveChatConfirmationOpen] = useState<boolean>(false);
    const [clearHistoryConfirmationOpen, setClearHistoryConfirmationOpen] =
        useState<boolean>(false);

    const handleChangeTheme = useCallback(() => {
        changeTheme();
    }, [changeTheme]);

    useEffect(() => {
        setSettings([
            {
                icon: TrashIcon,
                title: "Clear chat history",
                handler: () => {
                    setClearHistoryConfirmationOpen(true);
                }
            },
            {
                icon: MoonIcon,
                title: isLightTheme ? "Dark theme" : "Light theme",
                handler: handleChangeTheme
            }
        ]);
    }, [isLightTheme, handleChangeTheme]);

    useEffect(() => {
        if (newChat) {
            setChats(prevChats => [newChat, ...prevChats]);
        }
    }, [newChat]);

    useEffect(() => {
        if (user) {
            fetchRooms(user.uid).then(response => {
                const {
                    data: { items }
                } = response;
                setChats(items);

                if (chatId && !items.find((item: Chat) => item.roomId === chatId)) {
                    navigate("/new-chat");
                }
            });
        } else {
            setNewChat(null);
            setNewMessage(null);
        }
    }, [user, chatId, navigate]);

    const handleChatCreating = () => {
        navigate("/new-chat");
    };

    const handleChatDeleting = () => {
        setRemoveChatConfirmationOpen(false);

        if (!user || !settingChat) return;

        archiveRoom(user.uid, settingChat.roomId).then(() => {
            setChats(prevChats => prevChats.filter(chat => chat.roomId !== settingChat.roomId));
            setSettingChat(undefined);
            if (chatId === settingChat.roomId) {
                handleChatCreating();
            }
        });
    };

    const handleDeleteChatConfirmationAction = (chat: Chat) => {
        setSettingChat(chat);
        setRemoveChatConfirmationOpen(true);
    };

    const handleClearHistoryConfirmationAction = () => {
        if (!user) return;

        archiveAllRooms(user.uid).then(() => {
            setChats([]);
            handleChatCreating();
        });
        setClearHistoryConfirmationOpen(false);
    };

    return (
        <div className="chat-bot">
            <LeftMenuLayout
                leftMenu={
                    <ChatList
                        chats={chats}
                        settings={settings}
                        selectedChatId={chatId}
                        collapsedType={leftMenuCollapsed}
                        showAdminSettings={!!isAdmin}
                        handleChatDeleting={handleDeleteChatConfirmationAction}
                    />
                }
            >
                <HeaderLayout footer={<Footer setNewMessage={setNewMessage} />}>
                    {isNewChat ? (
                        <CreateChatMessages
                            newMessage={newMessage}
                            setNewMessage={setNewMessage}
                            setNewChat={setNewChat}
                        />
                    ) : (
                        <ChatMessages
                            newMessage={newMessage}
                            setNewMessage={setNewMessage}
                            placeholder={TivaPlaceholder}
                        />
                    )}
                </HeaderLayout>
            </LeftMenuLayout>
            <ConfirmationPopup
                open={clearHistoryConfirmationOpen}
                type={ConfirmationPopupType.dangerous}
                icon={TrashIcon}
                title="Clear history"
                description="Are you sure you want to delet all your chats?"
                actionName="Delete"
                handleAction={handleClearHistoryConfirmationAction}
                handleClose={() => setClearHistoryConfirmationOpen(false)}
            />
            <ConfirmationPopup
                open={removeChatConfirmationOpen}
                type={ConfirmationPopupType.dangerous}
                icon={TrashIcon}
                title="Delete"
                description={`Are you sure you want to delete "${settingChat?.title}"?`}
                actionName="Delete"
                handleAction={handleChatDeleting}
                handleClose={() => setRemoveChatConfirmationOpen(false)}
            />
        </div>
    );
};

export default ChatBot;
